import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  planScreenshots, generateScreenshot, generateFeatureGraphic, saveImageDraft, getScreenshotContext,
  type DraftImageItem, type ScreenshotScene, type ScreenshotPlan, type ScreenshotContext,
  type ScreenshotContextSwitches, type FeatureGraphicInput,
} from '@/api/play';
import { isRtlLocale } from '@/lib/rtlLocales';
import { Modal } from '../Modal';
import { Button } from '../Button';

/** How many frames a set defaults to — seven, one short of Play's cap of 8. */
const DEFAULT_COUNT = 7;
/** Play's cap on phone screenshots per locale. */
const MAX_PER_LOCALE = 8;

/** One drawn frame; `key`/`contentType` are there when the surface stores it itself. */
export interface ShotResult { url: string; key?: string; contentType?: string }

/** A drawn feature graphic and the tagline / composition it was drawn from. */
export interface FeatureShot { url: string; tagline: string; scene: string }

/**
 * The surface's feature-graphic slot. The banner is drawn in the set's style
 * guide, so banner and frames read as one campaign; `draw` also puts it in the
 * slot (the Play draft, the Share package), replacing what is there.
 */
export interface FeatureTarget {
  /** The slot already holds a banner — the checkbox then starts unticked. */
  exists: boolean;
  draw: (input: FeatureGraphicInput & ScreenshotContextSwitches) => Promise<FeatureShot>;
}

/**
 * Where a generated set goes. The Play images panel keeps frames in a draft it
 * rewrites after every frame; the Share tab's server attaches each frame to
 * the locale as it is drawn. The plan, the brand pack and the progress are the
 * same on both, so the dialog is one component.
 */
export interface ShotTarget {
  lang: string;
  /** Frames the locale already has. */
  existingCount: number;
  /** The set can be replaced here (Play drafts); elsewhere frames are only added. */
  canReplace: boolean;
  /** Short name of where frames land, for the notes: "draft", "package". */
  noun: string;
  contextKey: readonly unknown[];
  context: () => Promise<ScreenshotContext>;
  plan: (input: { lang: string; count: number; brief: string } & ScreenshotContextSwitches) => Promise<ScreenshotPlan>;
  draw: (input: {
    lang: string; style: string; subject: string; scene: string; caption: string; index: number; total: number;
  } & ScreenshotContextSwitches) => Promise<ShotResult>;
  /** Taken when the dialog opens: what `save` appends to. */
  snapshot?: () => unknown;
  /** Called after every frame with the whole session's frames, in plan order. */
  save?: (frames: ShotResult[], opts: { replace: boolean; snapshot: unknown }) => Promise<void>;
  /** The feature graphic drawn with the set, when the surface has a slot for it. */
  feature?: FeatureTarget;
}

/** The Play draft's feature graphic of one locale: drawn into draft media, then saved as the slot's draft. */
export function playFeatureTarget(appId: number, lang: string, exists: boolean, listing: {
  title: string; shortDescription: string; fullDescription: string;
}): FeatureTarget {
  return {
    exists,
    draw: async (input) => {
      const out = await generateFeatureGraphic(appId, {
        ...input, lang, title: listing.title, shortDescription: listing.shortDescription, fullDescription: listing.fullDescription,
      });
      await saveImageDraft(appId, lang, 'featureGraphic', [{ kind: 'media', key: out.key, contentType: out.contentType, url: out.url }]);
      return out;
    },
  };
}

/** The Play images panel's target: frames go into this locale's draft. */
export function playShotTarget(appId: number, lang: string, existing: DraftImageItem[], listing: {
  title: string; shortDescription: string; fullDescription: string;
}, feature?: FeatureTarget): ShotTarget {
  return {
    feature,
    lang,
    existingCount: existing.length,
    canReplace: true,
    noun: 'draft',
    contextKey: ['play-shot-context', appId],
    context: () => getScreenshotContext(appId),
    plan: (input) => planScreenshots(appId, {
      ...input, title: listing.title, shortDescription: listing.shortDescription, fullDescription: listing.fullDescription,
    }),
    draw: (input) => generateScreenshot(appId, input),
    snapshot: () => existing,
    save: async (frames, { replace, snapshot }) => {
      await saveImageDraft(appId, lang, 'phoneScreenshots', [
        ...(replace ? [] : (snapshot as DraftImageItem[])),
        ...frames.map((f): DraftImageItem => ({
          kind: 'media', key: f.key!, contentType: f.contentType ?? 'image/png', url: f.url,
        })),
      ]);
    },
  };
}

interface Row extends ScreenshotScene {
  id: number;
  selected: boolean;
  status: 'idle' | 'generating' | 'done' | 'error';
  result?: ShotResult;
  error?: string;
}

/** The banner row under the frames: tagline/scene are empty until the first draw writes them. */
interface BannerRow {
  selected: boolean;
  status: 'idle' | 'generating' | 'done' | 'error';
  /** undefined = let the AI write it; '' = the app name alone. */
  tagline?: string;
  scene: string;
  result?: FeatureShot;
  error?: string;
}

interface GenerateScreenshotsModalProps {
  isOpen: boolean;
  target: ShotTarget;
  onClose: () => void;
  onDone: () => void;
}

export function GenerateScreenshotsModal({ isOpen, target, onClose, onDone }: GenerateScreenshotsModalProps) {
  const { lang } = target;
  const [count, setCount] = useState(DEFAULT_COUNT);
  const [brief, setBrief] = useState('');
  const [style, setStyle] = useState('');
  // What the app is, from the plan. The style guide only fixes the look, so
  // this is what keeps a late frame on-brief instead of on-genre.
  const [subject, setSubject] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  // The locale as it was when the dialog opened. Saving refreshes the parent's
  // props mid-session, so reading them live would append this session's own
  // frames a second time.
  const [snapshot, setSnapshot] = useState<unknown>(null);
  const [existingCount, setExistingCount] = useState(target.existingCount);
  const [replace, setReplace] = useState(false);
  const [switches, setSwitches] = useState<ScreenshotContextSwitches>({ use_brand: true, ui_refs: false });
  const [planning, setPlanning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [banner, setBanner] = useState<BannerRow>({ selected: false, status: 'idle', scene: '' });
  const rtl = isRtlLocale(lang);

  const ctxQ = useQuery({ queryKey: target.contextKey, queryFn: target.context, enabled: isOpen, staleTime: 60_000 });
  const ctx = ctxQ.data;

  const freeSlots = replace ? MAX_PER_LOCALE : Math.max(MAX_PER_LOCALE - existingCount, 0);

  // Reopening starts a clean session: a stale plan from the previous open would
  // silently re-append frames the operator already saved. Keyed on isOpen
  // alone: the effect reads the props of the render that opened the dialog,
  // which is the state we want to hold for the whole session.
  useEffect(() => {
    if (!isOpen) return;
    setRows([]); setStyle(''); setSubject(''); setError(null); setSavedNote(null); setProgress(null);
    setSnapshot(target.snapshot?.() ?? null);
    setExistingCount(target.existingCount);
    // A full locale can only get a new set by replacing the old one.
    const full = target.existingCount >= MAX_PER_LOCALE;
    setReplace(target.canReplace && full);
    const free = target.canReplace && full ? MAX_PER_LOCALE : Math.max(MAX_PER_LOCALE - target.existingCount, 1);
    setCount(Math.min(DEFAULT_COUNT, free));
    // A slot without a banner gets one with the set; an existing one is only replaced on request.
    setBanner({ selected: !!target.feature && !target.feature.exists, status: 'idle', scene: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The switches follow the app's context once it loads: the brand pack on
  // when there is one, the real frames on when the wizard run used them.
  useEffect(() => {
    if (!ctx) return;
    setSwitches({ use_brand: ctx.source !== 'none', ui_refs: ctx.ui_refs_default && ctx.ref_count > 0 });
  }, [ctx]);

  useEffect(() => {
    if (count > Math.max(freeSlots, 1)) setCount(Math.max(freeSlots, 1));
  }, [freeSlots, count]);

  const setField = (id: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));

  const plan = async () => {
    setPlanning(true); setError(null); setSavedNote(null);
    try {
      const out = await target.plan({ lang, count, brief, ...switches });
      setStyle(out.style);
      setSubject(out.subject);
      setRows(out.scenes.map((s, i) => ({ ...s, id: i, selected: true, status: 'idle' })));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to plan the screenshots');
    } finally {
      setPlanning(false);
    }
  };

  /** Draws the banner in the set's style and puts it in the slot. Returns false on failure. */
  const drawBanner = async (row: BannerRow): Promise<boolean> => {
    if (!target.feature) return false;
    setBanner((b) => ({ ...b, status: 'generating', error: undefined }));
    try {
      const out = await target.feature.draw({
        style, subject, brief, ...switches,
        ...(row.tagline !== undefined ? { tagline: row.tagline } : {}),
        ...(row.scene.trim() ? { scene: row.scene } : {}),
      });
      setBanner((b) => ({ ...b, status: 'done', selected: false, result: out, tagline: out.tagline, scene: out.scene }));
      return true;
    } catch (e: any) {
      setBanner((b) => ({ ...b, status: 'error', error: e?.message ?? 'banner generation failed' }));
      return false;
    }
  };

  const runRows = async (targets: Row[], includeBanner = false) => {
    if (targets.length === 0 && !includeBanner) return;
    setError(null); setSavedNote(null);
    const steps = targets.length + (includeBanner ? 1 : 0);
    setProgress({ done: 0, total: steps });

    let latest = rows;
    const patch = (id: number, p: Partial<Row>) => {
      latest = latest.map((x) => x.id === id ? { ...x, ...p } : x);
      setRows(latest);
    };

    let failures = 0;
    for (let i = 0; i < targets.length; i++) {
      const r = latest.find((x) => x.id === targets[i].id)!;
      patch(r.id, { status: 'generating', error: undefined });
      try {
        const out = await target.draw({
          lang, style, subject, scene: r.scene, caption: r.caption, index: r.id, total: latest.length, ...switches,
        });
        patch(r.id, { status: 'done', selected: false, result: out });
        // Save as we go — each frame is a paid render, so it must survive the
        // next one failing or the operator closing the tab.
        if (target.save) {
          try {
            await target.save(latest.filter((x) => x.result).map((x) => x.result!), { replace, snapshot });
          } catch (e: any) {
            setError(e?.message ?? `Failed to save the ${target.noun}`);
          }
        }
      } catch (e: any) {
        failures++;
        patch(r.id, { status: 'error', error: e?.message ?? 'generation failed' });
      }
      setProgress({ done: i + 1, total: steps });
    }
    // The banner goes last: it reuses the style the frames were just drawn in.
    const bannerOk = includeBanner ? await drawBanner(banner) : false;
    setProgress(null);

    const made = latest.filter((r) => r.result).length;
    const notes: string[] = [];
    if (targets.length > 0 && made > 0) {
      notes.push(replace
        ? `Saved — the ${lang} ${target.noun} now holds the ${made} new screenshot(s).`
        : `Saved — ${made} screenshot(s) added to the ${lang} ${target.noun}.`);
    }
    if (bannerOk) notes.push(`Feature graphic saved to the ${target.noun}.`);
    if (notes.length > 0) {
      setSavedNote(notes.join(' '));
      onDone();
    }
    if (failures > 0) setError(`${failures} frame(s) failed — fix the scene and redo just those.`);
  };

  const selected = rows.filter((r) => r.selected);
  const generatedCount = rows.filter((r) => r.result).length;
  const overCap = generatedCount + selected.length > freeSlots;
  const busy = planning || !!progress;
  const withBanner = !!target.feature && banner.selected;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Generate screenshots — ${lang}`} size="lg">
      <p className="text-xs text-content-muted mb-3">
        One brief becomes a set: the model writes {count} different scenes sharing a single style, then draws each
        one with gpt-image-2. Captions come out in {lang}.{' '}
        {target.canReplace
          ? `Frames are saved to this locale's ${target.noun} — nothing reaches Google until you submit.`
          : `Each frame is attached to this locale's ${target.noun} as soon as it is drawn.`}
      </p>

      <ContextBox ctx={ctx} loading={ctxQ.isLoading} switches={switches} disabled={busy || rows.length > 0} onChange={setSwitches} />

      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div>
          <label className="block text-xs text-content-muted mb-1">How many</label>
          <select
            className="px-2 py-1 rounded bg-background border border-border text-content text-sm"
            value={count}
            disabled={busy || rows.length > 0}
            onChange={(e) => setCount(parseInt(e.target.value, 10))}
          >
            {Array.from({ length: Math.max(freeSlots, 1) }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <span className="text-xs text-content-subtle pb-1.5">{freeSlots} free slot(s)</span>
        {target.canReplace && existingCount > 0 && (
          <label className="flex items-center gap-1.5 text-xs text-content-muted pb-1.5">
            <input
              type="checkbox"
              checked={replace}
              disabled={busy || rows.length > 0 || existingCount >= MAX_PER_LOCALE}
              onChange={(e) => setReplace(e.target.checked)}
            />
            Replace the current {existingCount} screenshot(s)
            {existingCount >= MAX_PER_LOCALE && ' (the locale is full)'}
          </label>
        )}
      </div>

      <div className="mb-3">
        <label className="block text-xs text-content-muted mb-1">
          Brief (optional — the listing text{switches.use_brand && ctx?.source !== 'none' ? ' and the project brief' : ''} are sent automatically)
        </label>
        <textarea
          className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
          rows={3}
          value={brief}
          disabled={busy}
          placeholder="e.g. this release adds the bonus wheel — lead with it"
          onChange={(e) => setBrief(e.target.value)}
        />
      </div>

      {rows.length === 0 ? (
        <>
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button loading={planning} disabled={busy || freeSlots < 1} onClick={plan}>
              {planning ? 'Planning…' : `Plan ${count} screenshot(s)`}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="mb-3">
            <label className="block text-xs text-content-muted mb-1">Shared style (applied to every frame)</label>
            <textarea
              className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
              rows={2}
              value={style}
              disabled={!!progress}
              onChange={(e) => setStyle(e.target.value)}
            />
          </div>

          <div className="space-y-3 max-h-[22rem] overflow-y-auto">
            {rows.map((r) => (
              <div key={r.id} className="flex gap-3 items-start border border-border rounded-md p-2">
                <label className="pt-1">
                  <input
                    type="checkbox"
                    checked={r.selected}
                    disabled={!!progress}
                    onChange={(e) => setField(r.id, { selected: e.target.checked })}
                  />
                </label>
                {r.result && (
                  <img src={r.result.url} alt="" className="h-28 rounded border border-border object-cover shrink-0" />
                )}
                <div className="flex-1 space-y-2">
                  <div>
                    <label className="text-xs text-content-muted">Caption ({lang})</label>
                    <input
                      dir={rtl ? 'rtl' : 'ltr'}
                      className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
                      value={r.caption}
                      placeholder="Leave empty for an image with no text"
                      disabled={!!progress}
                      onChange={(e) => setField(r.id, { caption: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-content-muted">Scene</label>
                    <textarea
                      className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
                      rows={2}
                      value={r.scene}
                      disabled={!!progress}
                      onChange={(e) => setField(r.id, { scene: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === 'generating' && <span className="text-xs text-amber-400">Drawing…</span>}
                    {r.status === 'done' && <span className="text-xs text-green-500">Added to the {target.noun}</span>}
                    {r.error && <span className="text-xs text-red-400">{r.error}</span>}
                    {/* A redo on the Share tab would attach a second frame, not swap the first. */}
                    {(r.status === 'error' || (r.status === 'done' && target.save)) && !progress && (
                      <button
                        className="text-xs text-content-muted underline hover:text-content"
                        onClick={() => runRows([r])}
                      >
                        Redo this one
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {target.feature && (
            <div className="mt-3 flex gap-3 items-start border border-border rounded-md p-2">
              <label className="pt-1">
                <input
                  type="checkbox"
                  checked={banner.selected}
                  disabled={!!progress}
                  onChange={(e) => setBanner((b) => ({ ...b, selected: e.target.checked }))}
                />
              </label>
              {banner.result && (
                <img src={banner.result.url} alt="" className="h-20 rounded border border-border object-cover shrink-0" />
              )}
              <div className="flex-1 space-y-2">
                <div>
                  <p className="text-sm text-content">Feature graphic (1024×500)</p>
                  <p className="text-xs text-content-subtle">
                    Drawn in this set's style, by Play's rules: app name + tagline, focal point in the centre,
                    no phones, badges, "Free / #1 / Download" or prices.
                    {target.feature.exists && !banner.result && ' Replaces the current one.'}
                  </p>
                </div>
                {banner.tagline === undefined && !banner.result ? (
                  <button
                    type="button"
                    className="text-xs text-content-muted underline hover:text-content"
                    disabled={!!progress}
                    onClick={() => setBanner((b) => ({ ...b, tagline: '' }))}
                  >
                    Write the tagline myself
                  </button>
                ) : (
                  <div>
                    <label className="text-xs text-content-muted">Tagline ({lang}, under the app name — empty for the name alone)</label>
                    <input
                      dir={rtl ? 'rtl' : 'ltr'}
                      className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
                      value={banner.tagline ?? ''}
                      disabled={!!progress}
                      onChange={(e) => setBanner((b) => ({ ...b, tagline: e.target.value }))}
                    />
                  </div>
                )}
                {banner.result && (
                  <div>
                    <label className="text-xs text-content-muted">Composition</label>
                    <textarea
                      className="w-full px-2 py-1 rounded bg-background border border-border text-content text-sm"
                      rows={2}
                      value={banner.scene}
                      disabled={!!progress}
                      placeholder="Leave empty to let the AI compose a new one"
                      onChange={(e) => setBanner((b) => ({ ...b, scene: e.target.value }))}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {banner.status === 'generating' && <span className="text-xs text-amber-400">Drawing…</span>}
                  {banner.status === 'done' && <span className="text-xs text-green-500">Saved to the {target.noun}</span>}
                  {banner.error && <span className="text-xs text-red-400">{banner.error}</span>}
                  {(banner.status === 'error' || banner.status === 'done') && !progress && (
                    <button className="text-xs text-content-muted underline hover:text-content" onClick={() => runRows([], true)}>
                      Redo the banner
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {overCap && (
            <p className="text-amber-400 text-sm mt-2">
              That is more than the {freeSlots} free slot(s) — untick some frames.
            </p>
          )}
          {savedNote && <p className="text-green-500 text-sm mt-2">{savedNote}</p>}
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button
              loading={!!progress}
              disabled={!!progress || (selected.length === 0 && !withBanner) || overCap}
              onClick={() => runRows(selected, withBanner)}
            >
              {progress
                ? `Drawing ${progress.done}/${progress.total}…`
                : `Generate selected (${selected.length}${withBanner ? ' + banner' : ''})`}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** What the set is drawn from, with the two switches the operator may turn off. */
function ContextBox({ ctx, loading, switches, disabled, onChange }: {
  ctx: ScreenshotContext | undefined;
  loading: boolean;
  switches: ScreenshotContextSwitches;
  disabled: boolean;
  onChange: (s: ScreenshotContextSwitches) => void;
}) {
  if (loading) return <p className="text-xs text-content-subtle mb-3">Looking up the app's brand pack…</p>;
  if (!ctx) return null;
  const hasPack = ctx.source !== 'none';
  const facts = [
    ctx.shot_orient,
    ctx.phone_mockups ? 'phone mockups' : 'full-bleed',
    ctx.has_keywords ? 'keyword CTAs' : '',
  ].filter(Boolean).join(' · ');
  return (
    <div className="mb-3 rounded-md border border-border bg-background/40 p-2 text-xs space-y-1.5">
      {hasPack ? (
        <label className="flex items-start gap-1.5 text-content-muted">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={switches.use_brand}
            disabled={disabled}
            onChange={(e) => onChange({ ...switches, use_brand: e.target.checked })}
          />
          <span>
            Use the brand pack of <span className="text-content">{ctx.project_name ?? ctx.brand}</span>
            {ctx.source === 'wizard' && ' (as the wizard froze it — the project is gone)'}
            <span className="text-content-subtle"> — {ctx.brand} · {facts}</span>
          </span>
        </label>
      ) : (
        <p className="text-content-subtle">
          No Apps Setup project behind this app — the set is planned from the listing text{ctx.stub ? ' and the stub' : ''} only.
        </p>
      )}
      {ctx.stub && <p className="text-content-subtle">Stub: <span className="text-content-muted">{ctx.stub}</span></p>}
      {ctx.ref_count > 0 && (
        <label className="flex items-center gap-1.5 text-content-muted">
          <input
            type="checkbox"
            checked={switches.ui_refs}
            disabled={disabled}
            onChange={(e) => onChange({ ...switches, ui_refs: e.target.checked })}
          />
          Draw phone mockups around the stub's {ctx.ref_count} real UI frame(s)
        </label>
      )}
    </div>
  );
}
